import { CopyIcon, PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  BookingCategory,
  BookingQuestion,
  BookingType,
  computeCascadeOrder,
  getNextOrder,
  moveItem,
  OrderUpdate,
  sortByOrder,
  useGetBookingCategories,
  useGetBookingQuestions,
  useGetBookingTypes,
  useGetBookingTypesBuildings,
  useReorderBookingCategories,
  useReorderBookingQuestions,
  useReorderBookingTypes,
} from '@/api/booking-types'
import BuildingAnalyticIcons from '@/components/BuildingAnalytics/BuildingAnalyticIcons/BuildingAnalyticIcons'
import {
  LinkColorSelect,
  LinkedColumn,
  LinkedColumns,
  LinkStyleSelect,
  useLinkColor,
  useLinkStyle,
  ViewSettings,
  ViewSettingsMenu,
} from '@/components/layout/linked-columns'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { Switch } from '@/components/ui/switch'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { popupFactory, toastFactory } from '@/factories'
import { useDesignVersionToggle, useLocalStorage } from '@/hooks'
import { getPlaceName } from '@/pages/organisation/places/places.utils'
import { CountBadgeTooltip } from '@/pages/organisation/shared/count-badge-tooltip'
import { selectBuildings } from '@/reducers/buildingSelectorReducer'
import { theme } from '@/theme/theme'
import { getApiErrorMessage } from '@/utils/error'

import {
  BOOKING_TYPES_VIEW_STORAGE_KEY,
  DEFAULT_BOOKING_TYPES_VIEW,
} from './booking-types.constants'
import { useBookingDelete } from './booking-types.delete'
import { useBookingTypesSelection } from './booking-types.hooks'
import * as S from './booking-types.styled'
import { BookingTypeFormModal } from './modals/booking-type-form-modal'
import { GroupFormModal } from './modals/group-form-modal'
import { QuestionFormModal } from './modals/question-form-modal'
import { ANSWER_TYPE_LABEL_KEYS, getAnswersType } from './modals/question-form-modal.constants'

const EMPTY_GROUPS: BookingCategory[] = []
const EMPTY_TYPES: BookingType[] = []
const EMPTY_QUESTIONS: BookingQuestion[] = []

const COLUMN_LINKS = [
  { from: 'group', to: 'type' },
  { from: 'type', to: 'question' },
]

const getGroupTitle = (group: BookingCategory) => group.name
const getTypeTitle = (type: BookingType) => type.name
const getQuestionTitle = (question: BookingQuestion) => question.title

const countBy = <T,>(items: T[], getKey: (item: T) => number | undefined) =>
  items.reduce((counts, item) => {
    const key = getKey(item)
    if (key !== undefined) counts.set(key, (counts.get(key) ?? 0) + 1)
    return counts
  }, new Map<number, number>())

export const BookingTypesPage = () => {
  const { t } = useTranslation(['organisation', 'common'])

  const { redirect, toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'booking-types',
    screen: 'Booking Types',
    to: { path: '/dashboard/organisation/bookingtypes/legacy' },
    version: 'new',
  })

  const { handleDeleteGroup, handleDeleteQuestion, handleDeleteType } = useBookingDelete()

  const [draggingColumn, setDraggingColumn] = useState<string | null>(null)

  const [view, setView] = useLocalStorage(
    BOOKING_TYPES_VIEW_STORAGE_KEY,
    DEFAULT_BOOKING_TYPES_VIEW,
  )
  const [linkStyle, setLinkStyle] = useLinkStyle()
  const [linkColor, setLinkColor] = useLinkColor()

  const buildings = useSelector(selectBuildings)
  const buildingNames = useMemo(
    () => new Map(buildings.map((building) => [building.id, getPlaceName(building)])),
    [buildings],
  )

  const groupsQuery = useGetBookingCategories()
  const typesQuery = useGetBookingTypes()
  const questionsQuery = useGetBookingQuestions()

  const { mutate: reorderGroups } = useReorderBookingCategories()
  const { mutate: reorderTypes } = useReorderBookingTypes()
  const { mutate: reorderQuestions } = useReorderBookingQuestions()

  const groups = groupsQuery.data ?? EMPTY_GROUPS
  const types = typesQuery.data ?? EMPTY_TYPES
  const questions = questionsQuery.data ?? EMPTY_QUESTIONS

  const { selectGroup, selection, selectQuestion, selectType } = useBookingTypesSelection({
    groups,
    questions,
    types,
  })

  const sortedGroups = useMemo(() => sortByOrder(groups), [groups])

  const groupTypes = useMemo(
    () => sortByOrder(types.filter(({ category }) => category === selection.groupId)),
    [types, selection.groupId],
  )

  const typeQuestions = useMemo(
    () =>
      sortByOrder(questions.filter(({ bookingTypeId }) => bookingTypeId?.id === selection.typeId)),
    [questions, selection.typeId],
  )

  const selectedGroup = useMemo(
    () => groups.find(({ id }) => id === selection.groupId),
    [groups, selection.groupId],
  )
  const selectedType = useMemo(
    () => types.find(({ id }) => id === selection.typeId),
    [types, selection.typeId],
  )

  const buildingsByType = useGetBookingTypesBuildings(
    useMemo(() => groupTypes.map(({ id }) => id), [groupTypes]),
  )

  const typesPerGroup = useMemo(() => countBy(types, ({ category }) => category), [types])
  const questionsPerType = useMemo(
    () => countBy(questions, ({ bookingTypeId }) => bookingTypeId?.id),
    [questions],
  )

  if (redirect) return redirect

  const error = groupsQuery.error ?? typesQuery.error ?? questionsQuery.error

  const handleReorderError = (reorderError: unknown) =>
    toastFactory.error(getApiErrorMessage(reorderError))

  const handleReorder = (
    reorder: (updates: OrderUpdate[], options: { onError: (error: unknown) => void }) => void,
    items: { id: number; order: number }[],
    fromIndex: number,
    toIndex: number,
  ) =>
    reorder(computeCascadeOrder(moveItem(items, fromIndex, toIndex), toIndex), {
      onError: handleReorderError,
    })

  const getEditItem = (onClick: VoidFunction) => ({
    icon: PencilSimpleIcon,
    label: t('common:actions.edit'),
    onClick,
  })

  const getDeleteItem = (onClick: VoidFunction) => ({
    icon: TrashIcon,
    label: t('common:actions.delete'),
    onClick,
    variant: 'danger' as const,
  })

  const getDragProps = (dndType: string) => ({
    dimmed: draggingColumn !== null && draggingColumn !== dndType,
    onDragActiveChange: (active: boolean) => setDraggingColumn(active ? dndType : null),
  })

  const getQuestionSubtitle = (question: BookingQuestion) => {
    const answerType = getAnswersType(question)

    return [
      t(
        `organisation:bookingTypes.question.fields.answerType.options.${ANSWER_TYPE_LABEL_KEYS[answerType]}`,
      ),
      answerType === 'button'
        ? t('organisation:bookingTypes.columns.questions.answersCount', {
            count: question.answers.length,
          })
        : undefined,
      question.isRequired
        ? t('organisation:bookingTypes.columns.questions.required')
        : t('organisation:bookingTypes.columns.questions.optional'),
    ]
      .filter(Boolean)
      .join(' · ')
  }

  const handleSelectQuestion = (questionId: number) => {
    selectQuestion(questionId)
    const question = typeQuestions.find(({ id }) => id === questionId)
    if (question) handleEditQuestion(question)
  }

  const handleAddGroup = () =>
    popupFactory.open(GroupFormModal, { nextOrder: getNextOrder(groups), onSaved: selectGroup })

  const handleEditGroup = (group: BookingCategory) =>
    popupFactory.open(GroupFormModal, {
      group,
      nextOrder: getNextOrder(groups),
    })

  const handleAddType = () => {
    if (selection.groupId === null) return
    popupFactory.open(BookingTypeFormModal, {
      category: selection.groupId,
      nextOrder: getNextOrder(groupTypes),
      onSaved: selectType,
    })
  }

  const handleEditType = (bookingType: BookingType) =>
    popupFactory.open(BookingTypeFormModal, {
      bookingType,
      category: bookingType.category,
      nextOrder: getNextOrder(groupTypes),
    })

  const handleDuplicateType = (bookingType: BookingType) =>
    popupFactory.open(BookingTypeFormModal, {
      bookingType,
      category: bookingType.category,
      duplicate: true,
      nextOrder: getNextOrder(groupTypes),
      onSaved: selectType,
    })

  const handleAddQuestion = () => {
    if (selection.typeId === null) return
    popupFactory.open(QuestionFormModal, {
      bookingTypeId: selection.typeId,
      nextOrder: getNextOrder(typeQuestions),
      onSaved: selectQuestion,
    })
  }

  const handleEditQuestion = (question: BookingQuestion) =>
    popupFactory.open(QuestionFormModal, {
      bookingTypeId: question.bookingTypeId?.id ?? (selection.typeId as number),
      nextOrder: getNextOrder(typeQuestions),
      question,
    })

  const viewControls = (
    <>
      <Switch
        id="booking-types-view-show-icons"
        checked={view.showIcons}
        label={t('organisation:bookingTypes.view.showIcons')}
        size="small"
        onChange={(event) => setView({ ...view, showIcons: event.target.checked })}
      />
      <Switch
        id="booking-types-view-colorful-icons"
        checked={view.colorfulIcons}
        disabled={!view.showIcons}
        label={t('organisation:bookingTypes.view.colorfulIcons')}
        size="small"
        onChange={(event) => setView({ ...view, colorfulIcons: event.target.checked })}
      />
      <LinkStyleSelect value={linkStyle} onChange={setLinkStyle} />
      <LinkColorSelect value={linkColor} onChange={setLinkColor} />
    </>
  )

  return (
    <Page centered>
      <PageHeader
        title={t('organisation:bookingTypes.title')}
        actions={
          <S.HeaderActions>
            <ViewSettingsMenu>{viewControls}</ViewSettingsMenu>
            <ToggleSwitch
              checked
              falsyLabel={t('organisation:bookingTypes.designToggle.old')}
              truthyLabel={t('organisation:bookingTypes.designToggle.new')}
              onChange={handleUIToggle}
            />
          </S.HeaderActions>
        }
      />

      {error ? (
        <EmptyState
          description={getApiErrorMessage(error)}
          title={t('organisation:bookingTypes.error')}
        />
      ) : (
        <LinkedColumns
          dimmed={draggingColumn !== null}
          linkColor={linkColor}
          linkStyle={linkStyle}
          links={COLUMN_LINKS}
        >
          <LinkedColumn
            addLabel={t('organisation:bookingTypes.columns.groups.add')}
            dndType="group"
            {...getDragProps('group')}
            emptyDescription={t('organisation:bookingTypes.columns.groups.empty.description')}
            emptyTitle={t('organisation:bookingTypes.columns.groups.empty.title')}
            getMenuItems={(group) => [
              getEditItem(() => handleEditGroup(group)),
              getDeleteItem(() => handleDeleteGroup(group, typesPerGroup.get(group.id) ?? 0)),
            ]}
            getSubtitle={(group) =>
              t('organisation:bookingTypes.columns.groups.subtitle', {
                count: typesPerGroup.get(group.id) ?? 0,
              })
            }
            getTitle={getGroupTitle}
            items={sortedGroups}
            loading={groupsQuery.isLoading}
            selectedId={selection.groupId}
            title={t('organisation:bookingTypes.columns.groups.title')}
            onAdd={handleAddGroup}
            onReorder={(from, to) => handleReorder(reorderGroups, sortedGroups, from, to)}
            onEdit={handleEditGroup}
            onSelect={selectGroup}
          />

          <LinkedColumn
            addLabel={t('organisation:bookingTypes.columns.types.add')}
            dndType="type"
            {...getDragProps('type')}
            emptyDescription={t('organisation:bookingTypes.columns.types.empty.description')}
            emptyTitle={t('organisation:bookingTypes.columns.types.empty.title')}
            getSubtitleEnd={(type) => {
              const typeBuildings = buildingsByType.get(type.id)
              if (!typeBuildings) return <S.BuildingsTextSkeleton height="1.2rem" width="6rem" />
              if (typeBuildings.length === 0) return null

              return (
                <CountBadgeTooltip
                  scrollable
                  openOn="click"
                  variant="text"
                  badgeLabel={t('organisation:bookingTypes.columns.types.buildingsCount', {
                    count: typeBuildings.length,
                  })}
                  limit={typeBuildings.length}
                  items={typeBuildings
                    .map(({ id, name }) => buildingNames.get(id) ?? name ?? String(id))
                    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))}
                />
              )
            }}
            getLeading={
              view.showIcons
                ? (type) => (
                    <S.TypeIcon
                      $color={type.color || theme.colors.black700}
                      $colorful={view.colorfulIcons}
                    >
                      {type.icon && <BuildingAnalyticIcons name={type.icon} />}
                    </S.TypeIcon>
                  )
                : undefined
            }
            getMenuItems={(type) => [
              getEditItem(() => handleEditType(type)),
              {
                icon: CopyIcon,
                label: t('organisation:bookingTypes.actions.duplicate'),
                onClick: () => handleDuplicateType(type),
              },
              getDeleteItem(() => handleDeleteType(type, questionsPerType.get(type.id) ?? 0)),
            ]}
            getSubtitle={(type) =>
              t('organisation:bookingTypes.columns.types.subtitle', {
                count: questionsPerType.get(type.id) ?? 0,
              })
            }
            getTitle={getTypeTitle}
            items={groupTypes}
            loading={typesQuery.isLoading}
            selectedId={selection.typeId}
            missingParent={
              selection.groupId === null
                ? {
                    description: t('organisation:bookingTypes.columns.types.noParent.description'),
                    hint: t('organisation:bookingTypes.columns.types.noParent.hint'),
                    title: t('organisation:bookingTypes.columns.types.noParent.title'),
                  }
                : undefined
            }
            title={
              selectedGroup
                ? t('organisation:bookingTypes.columns.types.titleIn', {
                    parent: selectedGroup.name,
                  })
                : t('organisation:bookingTypes.columns.types.title')
            }
            onAdd={selection.groupId === null ? undefined : handleAddType}
            onReorder={(from, to) => handleReorder(reorderTypes, groupTypes, from, to)}
            onEdit={handleEditType}
            onSelect={selectType}
          />

          <LinkedColumn
            numbered
            addLabel={t('organisation:bookingTypes.columns.questions.add')}
            dndType="question"
            {...getDragProps('question')}
            emptyDescription={t('organisation:bookingTypes.columns.questions.empty.description')}
            emptyTitle={t('organisation:bookingTypes.columns.questions.empty.title')}
            getMenuItems={(question) => [
              getEditItem(() => handleEditQuestion(question)),
              getDeleteItem(() => handleDeleteQuestion(question)),
            ]}
            getSubtitle={getQuestionSubtitle}
            getTitle={getQuestionTitle}
            items={typeQuestions}
            loading={questionsQuery.isLoading}
            selectedId={selection.questionId}
            missingParent={
              selection.typeId === null
                ? {
                    description: t(
                      'organisation:bookingTypes.columns.questions.noParent.description',
                    ),
                    hint: t('organisation:bookingTypes.columns.questions.noParent.hint'),
                    title: t('organisation:bookingTypes.columns.questions.noParent.title'),
                  }
                : undefined
            }
            title={
              selectedType
                ? t('organisation:bookingTypes.columns.questions.titleIn', {
                    parent: selectedType.name,
                  })
                : t('organisation:bookingTypes.columns.questions.title')
            }
            onAdd={selection.typeId === null ? undefined : handleAddQuestion}
            onReorder={(from, to) => handleReorder(reorderQuestions, typeQuestions, from, to)}
            onEdit={handleEditQuestion}
            onSelect={handleSelectQuestion}
          />

          <ViewSettings>{viewControls}</ViewSettings>
        </LinkedColumns>
      )}
    </Page>
  )
}
